import { state } from '../state.js';

// ---------------------------------------------------------------------------
// Tab Hotkeys — keyboard shortcuts for the top-panel action buttons of each
// arbiter tab. Entirely separate from the result-entry hotkeys (those live in
// pairings.js / the Register Results modal and use the `resultsHotkeys` key).
//
// A shortcut is either "one button" (a single key, e.g. P) or "two button"
// (a modifier — Ctrl/Alt/Shift — plus a key, e.g. Alt+P). Shortcuts only fire
// while their owning tab is the active view, the user isn't typing in a field,
// and no modal is open. Bindings start empty and are device-local.
// ---------------------------------------------------------------------------

// Per-tab registry of the top-panel buttons that can be bound. The `id` must
// match the button's element id in index.html; the matched binding simply
// `.click()`s that button so all existing behaviour is reused.
const NAV_HOTKEY_GROUPS = [
    {
        view: 'dashboard', label: 'Dashboard', actions: [
            { id: 'btn-check-tournament', label: 'Check Tournament' },
            { id: 'btn-edit-tournament', label: 'Options' },
            { id: 'btn-close-tournament', label: 'Close Tournament' }
        ]
    },
    {
        view: 'tournaments', label: 'My Tournaments', actions: [
            { id: 'btn-import-tournament', label: 'Import Tournament' },
            { id: 'btn-export-tournament', label: 'Export Tournament' },
            { id: 'btn-new-folder', label: 'New Folder' },
            { id: 'btn-create-tournament-page', label: 'Create New Tournament' }
        ]
    },
    {
        view: 'players', label: 'Players', actions: [
            { id: 'btn-view-columns', label: 'View Columns' },
            { id: 'btn-toggle-all-status', label: 'Toggle All Status' },
            { id: 'btn-assign-snr', label: 'Assign SNR' },
            { id: 'btn-player-stats', label: 'Statistics' },
            { id: 'btn-print-players', label: 'Print List' },
            { id: 'btn-add-player', label: 'Add Player' }
        ]
    },
    {
        view: 'pairings', label: 'Pairings', actions: [
            { id: 'btn-generate-round', label: 'Generate Round' },
            { id: 'btn-undo-round', label: 'Undo Last Round' },
            { id: 'btn-edit-pairings', label: 'Edit Pairings' },
            { id: 'btn-register-results', label: 'Register Results' },
            { id: 'btn-view-unpaired', label: 'View Unpaired' },
            { id: 'btn-print-pairings', label: 'Print Pairings' },
            { id: 'btn-fullscreen-pairings', label: 'Full Screen' }
        ]
    },
    {
        view: 'standings', label: 'Standings', actions: [
            { id: 'btn-print-standings', label: 'Print Standings' },
            { id: 'btn-fullscreen-standings', label: 'Full Screen' },
            { id: 'btn-edit-tiebreaks', label: 'Edit Tie-Breaks' }
        ]
    }
];

// Persisted bindings: { [buttonId]: { key, ctrl, alt, shift } }
let navHotkeysState = {};

// Editor-only working copies (committed to navHotkeysState on Save)
let tempBindings = {};
let editorModes = {};          // { [buttonId]: 'one' | 'two' }
let recordingId = null;        // button id currently capturing a key
let recordHint = '';           // transient hint shown in the recording block
let activeEditorView = 'dashboard';

// --- key helpers -----------------------------------------------------------

const MODIFIER_KEYS = ['Control', 'Alt', 'Shift', 'Meta', 'OS', 'AltGraph'];
function isModifierKey(k) {
    return MODIFIER_KEYS.includes(k);
}

// Canonical key name so capture and runtime matching agree (letters lowercased,
// space named, everything else — Enter/F2/ArrowUp — kept verbatim).
function normalizeKey(e) {
    if (e.key === ' ' || e.code === 'Space') return 'Space';
    if (e.key.length === 1) return e.key.toLowerCase();
    return e.key;
}

function formatBinding(b) {
    if (!b || !b.key) return '';
    const parts = [];
    if (b.ctrl) parts.push('Ctrl');
    if (b.alt) parts.push('Alt');
    if (b.shift) parts.push('Shift');
    let k = b.key;
    if (k.length === 1) k = k.toUpperCase();
    parts.push(k);
    return parts.join(' + ');
}

function bindingsEqual(a, b) {
    if (!a || !b) return false;
    return a.key === b.key && !!a.ctrl === !!b.ctrl && !!a.alt === !!b.alt && !!a.shift === !!b.shift;
}

function matchesBinding(e, b) {
    if (!b || !b.key) return false;
    if (normalizeKey(e) !== b.key) return false;
    return (!!b.ctrl === e.ctrlKey) && (!!b.alt === e.altKey) && (!!b.shift === e.shiftKey) && !e.metaKey;
}

// --- persistence -----------------------------------------------------------

function loadNavHotkeys() {
    try {
        const saved = localStorage.getItem('navHotkeys');
        if (saved && saved !== 'undefined') {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === 'object') navHotkeysState = parsed;
        }
    } catch (e) {
        console.error('Error loading nav hotkeys:', e);
    }
}

function saveNavHotkeys() {
    try {
        localStorage.setItem('navHotkeys', JSON.stringify(navHotkeysState));
    } catch (e) {
        console.error('Error saving nav hotkeys:', e);
    }
}

// --- editor rendering ------------------------------------------------------

function renderTabSelector() {
    const tabsEl = document.getElementById('nav-hotkeys-tabs');
    if (!tabsEl) return;

    tabsEl.innerHTML = NAV_HOTKEY_GROUPS.map(g => {
        const active = g.view === activeEditorView;
        const style = active
            ? 'background: var(--primary); color: #fff; font-weight: 600; border-color: var(--primary);'
            : 'background: transparent; color: var(--text-main);';
        return `<button type="button" class="nav-hk-tab" data-view="${g.view}" style="padding: 0.4rem 0.8rem; font-size: 0.78rem; border-radius: 6px; cursor: pointer; border: 1px solid var(--border-color); transition: all 0.15s; ${style}">${tr(g.label)}</button>`;
    }).join('');

    tabsEl.querySelectorAll('.nav-hk-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            activeEditorView = btn.dataset.view;
            recordingId = null;
            recordHint = '';
            renderNavHotkeysList();
        });
    });
}

function renderNavHotkeysList() {
    renderTabSelector();

    const list = document.getElementById('nav-hotkeys-list');
    if (!list) return;

    const group = NAV_HOTKEY_GROUPS.find(g => g.view === activeEditorView);
    if (!group) return;

    list.innerHTML = group.actions.map(a => {
        const b = tempBindings[a.id];
        const mode = editorModes[a.id] || 'one';
        const isRec = recordingId === a.id;

        const recordLabel = isRec
            ? (recordHint || (mode === 'two' ? tr('Press Ctrl/Alt/Shift + key…') : tr('Press a key…')))
            : (b ? formatBinding(b) : tr('NONE'));

        const recStyle = isRec
            ? 'border: 2px solid var(--primary); background: rgba(59, 130, 246, 0.15); color: var(--text-main); font-weight: 700; box-shadow: 0 0 8px rgba(59, 130, 246, 0.3);'
            : `border: 1px solid var(--border-color); background: var(--surface-tint); color: ${b ? 'var(--text-main)' : 'var(--text-muted)'}; font-weight: 700;`;

        const modeBtn = (m, txt) => {
            const on = mode === m;
            const st = on
                ? 'background: var(--primary); color: #fff;'
                : 'background: transparent; color: var(--text-muted);';
            return `<button type="button" class="nav-hk-mode" data-id="${a.id}" data-mode="${m}" style="border: none; padding: 0.25rem 0.5rem; font-size: 0.7rem; border-radius: 4px; cursor: pointer; ${st}">${txt}</button>`;
        };

        return `
            <div style="display: flex; align-items: center; gap: 0.6rem; padding: 0.5rem 0.75rem; border-radius: 8px; border: 1px solid var(--border-color); background: var(--surface-tint-faint);">
                <div style="flex: 1; min-width: 0; font-size: 0.85rem; font-weight: 600; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${tr(a.label)}</div>
                <div style="display: flex; gap: 2px; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 6px; padding: 2px; flex-shrink: 0;">
                    ${modeBtn('one', tr('1 Key'))}
                    ${modeBtn('two', tr('2 Keys'))}
                </div>
                <button type="button" class="nav-hk-record" data-id="${a.id}" style="flex-shrink: 0; min-width: 150px; height: 32px; font-size: 0.72rem; border-radius: 6px; cursor: pointer; transition: all 0.15s; padding: 0 0.5rem; ${recStyle}">${recordLabel}</button>
                <button type="button" class="nav-hk-clear" data-id="${a.id}" title="Clear shortcut" style="flex-shrink: 0; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: 1px solid var(--border-color); background: transparent; color: var(--text-muted); border-radius: 6px; cursor: pointer;"><i class="ri-close-line"></i></button>
            </div>
        `;
    }).join('');

    // Mode toggle: switching a row's mode clears its current binding so the
    // user re-records under the new rule (a 1-key binding can't carry a
    // modifier; a 2-key one requires one).
    list.querySelectorAll('.nav-hk-mode').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            const newMode = btn.dataset.mode;
            if (editorModes[id] === newMode) return;
            editorModes[id] = newMode;
            delete tempBindings[id];
            if (recordingId === id) recordingId = null;
            recordHint = '';
            renderNavHotkeysList();
        });
    });

    // Record block: toggle capture mode for that row.
    list.querySelectorAll('.nav-hk-record').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            recordingId = (recordingId === id) ? null : id;
            recordHint = '';
            renderNavHotkeysList();
        });
    });

    // Clear button: unbind that row.
    list.querySelectorAll('.nav-hk-clear').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            delete tempBindings[id];
            if (recordingId === id) recordingId = null;
            recordHint = '';
            renderNavHotkeysList();
        });
    });
}

function openNavHotkeysModal() {
    tempBindings = JSON.parse(JSON.stringify(navHotkeysState || {}));
    editorModes = {};
    NAV_HOTKEY_GROUPS.forEach(g => g.actions.forEach(a => {
        const b = tempBindings[a.id];
        editorModes[a.id] = (b && (b.ctrl || b.alt || b.shift)) ? 'two' : 'one';
    }));
    recordingId = null;
    recordHint = '';
    renderNavHotkeysList();

    const modal = document.getElementById('nav-hotkeys-modal');
    if (modal) modal.classList.add('active');
}

// --- key capture (only while the editor is open + a row is recording) ------

function onRecordKeydown(e) {
    const modal = document.getElementById('nav-hotkeys-modal');
    if (!modal || !modal.classList.contains('active')) return;
    if (!recordingId) return;

    e.preventDefault();
    e.stopPropagation();

    if (e.key === 'Escape') {
        recordingId = null;
        recordHint = '';
        renderNavHotkeysList();
        return;
    }

    // Ignore lone modifier presses — wait for the actual key.
    if (isModifierKey(e.key)) return;

    const mode = editorModes[recordingId] || 'one';
    const key = normalizeKey(e);

    let binding;
    if (mode === 'two') {
        if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
            recordHint = tr('Hold Ctrl, Alt or Shift');
            renderNavHotkeysList();
            return;
        }
        binding = { key, ctrl: e.ctrlKey, alt: e.altKey, shift: e.shiftKey };
    } else {
        binding = { key, ctrl: false, alt: false, shift: false };
    }

    // No two actions on the same tab may share a shortcut (they fire in the
    // same view). The same key is fine across different tabs.
    const group = NAV_HOTKEY_GROUPS.find(g => g.actions.some(a => a.id === recordingId));
    const dup = group.actions.find(a => a.id !== recordingId && bindingsEqual(tempBindings[a.id], binding));
    if (dup) {
        recordingId = null;
        recordHint = '';
        renderNavHotkeysList();
        window.Dialog.warn(
            tr('"{key}" is already assigned to "{action}" on this tab. Choose a different shortcut.', { key: formatBinding(binding), action: tr(dup.label) }),
            tr('Shortcut Conflict')
        );
        return;
    }

    tempBindings[recordingId] = binding;
    recordingId = null;
    recordHint = '';
    renderNavHotkeysList();
}

// --- runtime: fire the matching button on the active tab -------------------

function onGlobalKeydown(e) {
    if (e.repeat) return;
    if (isModifierKey(e.key)) return;
    if (state.isSpectatorMode) return;

    // Don't steal keys from text entry or while any modal (incl. the editor) is open.
    if (document.querySelector('.modal-overlay.active')) return;
    const ae = document.activeElement;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT' || ae.isContentEditable)) return;

    const activeView = document.querySelector('.view.active');
    if (!activeView) return;
    const group = NAV_HOTKEY_GROUPS.find(g => g.view === activeView.id);
    if (!group) return;

    for (const a of group.actions) {
        if (matchesBinding(e, navHotkeysState[a.id])) {
            const btn = document.getElementById(a.id);
            // Only trigger a button that's actually visible + enabled on screen.
            if (btn && btn.offsetParent !== null && !btn.disabled) {
                e.preventDefault();
                btn.click();
            }
            return;
        }
    }
}

export function initNavHotkeys() {
    loadNavHotkeys();

    const openBtn = document.getElementById('btn-edit-nav-hotkeys');
    if (openBtn) openBtn.addEventListener('click', openNavHotkeysModal);

    const saveBtn = document.getElementById('btn-save-nav-hotkeys');
    if (saveBtn) saveBtn.addEventListener('click', () => {
        // Drop empty entries so the store stays tidy.
        const clean = {};
        Object.entries(tempBindings).forEach(([id, b]) => { if (b && b.key) clean[id] = b; });
        navHotkeysState = clean;
        saveNavHotkeys();
        const modal = document.getElementById('nav-hotkeys-modal');
        if (modal) modal.classList.remove('active');
    });

    const clearBtn = document.getElementById('btn-clear-nav-hotkeys');
    if (clearBtn) clearBtn.addEventListener('click', () => {
        tempBindings = {};
        NAV_HOTKEY_GROUPS.forEach(g => g.actions.forEach(a => { editorModes[a.id] = 'one'; }));
        recordingId = null;
        recordHint = '';
        renderNavHotkeysList();
    });

    // Capture phase so a recorded Enter/Space can't also trigger document-level
    // handlers (e.g. the modal Enter-to-submit) before we consume it.
    window.addEventListener('keydown', onRecordKeydown, true);
    window.addEventListener('keydown', onGlobalKeydown);
}
